import React, { useEffect, useState } from 'react';
import { Megaphone, Edit2, Trash2, X, CheckCircle, AlertCircle } from 'lucide-react';
import AuroraFloatingCard from '../ui/AuroraFloatingCard';
// Define types for announcements
interface Announcement {
  id: number;
  title: string;
  content: string;
  targets: {
    students: boolean;
    parents: boolean;
    teachers: boolean;
  };
  date: string;
}
export function DuyuruYonetimi() {
  // State for form inputs
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [targetStudents, setTargetStudents] = useState(false);
  const [targetParents, setTargetParents] = useState(false);
  const [targetTeachers, setTargetTeachers] = useState(false);
  // State for announcements list
  const [announcements, setAnnouncements] = useState<Announcement[]>([{
    id: 1,
    title: 'LGS Deneme Sınavı Hakkında',
    content: "Bu hafta Cumartesi günü tüm 8. sınıf öğrencileri için LGS deneme sınavı yapılacaktır. Sınav saat 09:00'da başlayacak ve 12:30'da sona erecektir. Tüm öğrencilerimizin sınavdan 30 dakika önce okulda bulunmaları gerekmektedir.",
    targets: {
      students: true,
      parents: true,
      teachers: false
    },
    date: '15 Ekim 2023 - 10:45'
  }, {
    id: 2,
    title: 'Veli Toplantısı Erteleme',
    content: 'Bu hafta Çarşamba günü yapılması planlanan veli toplantısı, öğretmenler kurulu toplantısı nedeniyle gelecek haftaya ertelenmiştir. Yeni toplantı tarihi ve saati ayrıca duyurulacaktır.',
    targets: {
      students: false,
      parents: true,
      teachers: true
    },
    date: '12 Ekim 2023 - 14:20'
  }, {
    id: 3,
    title: 'Karne Günü ve Yaz Tatili',
    content: "Değerli veli ve öğrencilerimiz, 18 Haziran Cuma günü karne dağıtım töreni yapılacaktır. Tüm öğrencilerimizin saat 10:00'da okulda bulunmaları gerekmektedir. Herkese iyi tatiller dileriz.",
    targets: {
      students: true,
      parents: true,
      teachers: true
    },
    date: '10 Ekim 2023 - 09:30'
  }]);
  // State for tracking edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  // State for confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<Announcement | null>(null);
  // State for success/error messages
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    show: false,
    message: '',
    type: 'success'
  });
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    if (!title.trim() || !content.trim() || !(targetStudents || targetParents || targetTeachers)) {
      setNotification({
        show: true,
        message: 'Lütfen tüm alanları doldurun ve en az bir hedef kitle seçin.',
        type: 'error'
      });
      return;
    }
    // Format current date
    const now = new Date();
    const formattedDate = `${now.getDate()} ${getMonthName(now.getMonth())} ${now.getFullYear()} - ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    if (isEditing && editingId) {
      // Update existing announcement
      setAnnouncements(announcements.map(announcement => announcement.id === editingId ? {
        ...announcement,
        title,
        content,
        targets: {
          students: targetStudents,
          parents: targetParents,
          teachers: targetTeachers
        },
        date: formattedDate
      } : announcement));
      setNotification({
        show: true,
        message: 'Duyuru başarıyla güncellendi.',
        type: 'success'
      });
    } else {
      // Create new announcement
      const newAnnouncement: Announcement = {
        id: announcements.length > 0 ? Math.max(...announcements.map(a => a.id)) + 1 : 1,
        title,
        content,
        targets: {
          students: targetStudents,
          parents: targetParents,
          teachers: targetTeachers
        },
        date: formattedDate
      };
      setAnnouncements([newAnnouncement, ...announcements]);
      setNotification({
        show: true,
        message: 'Duyuru başarıyla gönderildi.',
        type: 'success'
      });
    }
    // Reset form
    resetForm();
  };
  // Helper function to get month name
  const getMonthName = (monthIndex: number) => {
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    return months[monthIndex];
  };
  // Reset form fields
  const resetForm = () => {
    setTitle('');
    setContent('');
    setTargetStudents(false);
    setTargetParents(false);
    setTargetTeachers(false);
    setIsEditing(false);
    setEditingId(null);
  };
  // Handle edit button click
  const handleEdit = (announcement: Announcement) => {
    setTitle(announcement.title);
    setContent(announcement.content);
    setTargetStudents(announcement.targets.students);
    setTargetParents(announcement.targets.parents);
    setTargetTeachers(announcement.targets.teachers);
    setIsEditing(true);
    setEditingId(announcement.id);
  };
  // Handle delete button click
  const handleDeleteClick = (announcement: Announcement) => {
    setAnnouncementToDelete(announcement);
    setShowDeleteModal(true);
  };
  // Confirm deletion
  const confirmDelete = () => {
    if (announcementToDelete) {
      setAnnouncements(announcements.filter(a => a.id !== announcementToDelete.id));
      setNotification({
        show: true,
        message: 'Duyuru başarıyla silindi.',
        type: 'success'
      });
    }
    setShowDeleteModal(false);
    setAnnouncementToDelete(null);
  };
  // Hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({
          ...notification,
          show: false
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);
  // Format target groups for display
  const formatTargets = (targets: {
    students: boolean;
    parents: boolean;
    teachers: boolean;
  }) => {
    const groups = [];
    if (targets.students) groups.push('Öğrenciler');
    if (targets.parents) groups.push('Veliler');
    if (targets.teachers) groups.push('Öğretmenler');
    return groups.join(', ');
  };
  return <div className="space-y-6" data-id="element-278">
      {/* Page Header */}
      <div className="flex items-center gap-3" data-id="element-279">
        <h1 className="text-3xl font-bold" data-id="element-280">Duyuru Yönetimi</h1>
        <Megaphone className="w-8 h-8 text-pink-400" data-id="element-281" />
      </div>
      {/* Notification */}
      {notification.show && <div className={`fixed top-6 right-6 z-50 p-4 rounded-lg shadow-lg max-w-md flex items-center gap-3 animate-slide-in-right ${notification.type === 'success' ? 'bg-green-900/70 text-green-100 border border-green-500/30' : 'bg-red-900/70 text-red-100 border border-red-500/30'}`} data-id="element-282">
          {notification.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-283" /> : <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" data-id="element-284" />}
          <span data-id="element-285">{notification.message}</span>
        </div>}
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-286">
        {/* Left Column: Create New Announcement Form (40%) */}
        <div className="lg:col-span-2" data-id="element-287">
          <AuroraFloatingCard data-id="element-288">
            <div className="p-6" data-id="element-289">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2" data-id="element-290">
                <Megaphone className="w-5 h-5 text-pink-400" data-id="element-291" />
                {isEditing ? 'Duyuruyu Düzenle' : 'Yeni Duyuru Oluştur'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-292">
                {/* Title Field */}
                <div data-id="element-293">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-294">
                    Duyuru Başlığı
                  </label>
                  <input type="text" id="title" value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-[#1C2333] text-white rounded-lg p-3 border border-purple-500/30 focus:border-blue-500 focus:ring focus:ring-blue-500/50 focus:outline-none" placeholder="Duyuru başlığını girin..." required data-id="element-295" />
                </div>
                {/* Content Field */}
                <div data-id="element-296">
                  <label htmlFor="content" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-297">
                    Duyuru İçeriği
                  </label>
                  <textarea id="content" value={content} onChange={e => setContent(e.target.value)} className="w-full h-40 bg-[#1C2333] text-white rounded-lg p-3 border border-purple-500/30 focus:border-blue-500 focus:ring focus:ring-blue-500/50 focus:outline-none" placeholder="Duyuru içeriğini girin..." required data-id="element-298" />
                </div>
                {/* Target Audience */}
                <div data-id="element-299">
                  <p className="block text-sm font-medium text-gray-300 mb-2" data-id="element-300">
                    Kime Gönderilsin?
                  </p>
                  <div className="flex flex-wrap gap-4" data-id="element-301">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-302">
                      <input type="checkbox" checked={targetStudents} onChange={e => setTargetStudents(e.target.checked)} className="w-4 h-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-700" data-id="element-303" />
                      <span data-id="element-304">Tüm Öğrenciler</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-305">
                      <input type="checkbox" checked={targetParents} onChange={e => setTargetParents(e.target.checked)} className="w-4 h-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-700" data-id="element-306" />
                      <span data-id="element-307">Tüm Veliler</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-308">
                      <input type="checkbox" checked={targetTeachers} onChange={e => setTargetTeachers(e.target.checked)} className="w-4 h-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-700" data-id="element-309" />
                      <span data-id="element-310">Tüm Öğretmenler</span>
                    </label>
                  </div>
                </div>
                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4" data-id="element-311">
                  {isEditing && <button type="button" onClick={resetForm} className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors" data-id="element-312">
                      İptal
                    </button>}
                  <button type="submit" className="px-6 py-2 rounded-lg bg-gradient-to-r from-pink-600 to-purple-600 hover:opacity-90 text-white font-medium transition-colors" data-id="element-313">
                    {isEditing ? 'Değişiklikleri Kaydet' : 'Duyuruyu Gönder'}
                  </button>
                </div>
              </form>
            </div>
          </AuroraFloatingCard>
        </div>
        {/* Right Column: Sent Announcements (60%) */}
        <div className="lg:col-span-3" data-id="element-314">
          <AuroraFloatingCard data-id="element-315">
            <div className="p-6" data-id="element-316">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2" data-id="element-317">
                <Megaphone className="w-5 h-5 text-purple-400" data-id="element-318" />
                Gönderilmiş Duyurular
              </h2>
              <div className="space-y-4" data-id="element-319">
                {announcements.length > 0 ? announcements.map(announcement => <div key={announcement.id} className="p-4 rounded-lg bg-[#1C2333] border border-purple-500/20 hover:border-purple-500/40 transition-colors group relative" data-id="element-320">
                      {/* Announcement Content */}
                      <h3 className="text-lg font-bold text-white mb-2" data-id="element-321">
                        {announcement.title}
                      </h3>
                      <p className="text-gray-300 mb-3 text-sm line-clamp-2" data-id="element-322">
                        {announcement.content}
                      </p>
                      <div className="flex flex-wrap justify-between items-end" data-id="element-323">
                        <div data-id="element-324">
                          <p className="text-xs text-blue-400" data-id="element-325">
                            Gönderilen Gruplar:{' '}
                            {formatTargets(announcement.targets)}
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-326">
                            {announcement.date}
                          </p>
                        </div>
                        {/* Action Buttons */}
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-327">
                          <button onClick={() => handleEdit(announcement)} className="p-2 rounded hover:bg-gray-700/50" title="Düzenle" data-id="element-328">
                            <Edit2 className="w-4 h-4 text-blue-400" data-id="element-329" />
                          </button>
                          <button onClick={() => handleDeleteClick(announcement)} className="p-2 rounded hover:bg-gray-700/50" title="Sil" data-id="element-330">
                            <Trash2 className="w-4 h-4 text-red-400" data-id="element-331" />
                          </button>
                        </div>
                      </div>
                    </div>) : <div className="text-center py-10" data-id="element-332">
                    <Megaphone className="w-12 h-12 text-gray-600 mx-auto mb-4" data-id="element-333" />
                    <p className="text-gray-400" data-id="element-334">
                      Henüz gönderilmiş duyuru bulunmuyor.
                    </p>
                  </div>}
              </div>
            </div>
          </AuroraFloatingCard>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-id="element-335">
          <AuroraFloatingCard className="w-full max-w-md" data-id="element-336">
            <div className="p-6" data-id="element-337">
              <h3 className="text-lg font-bold mb-4" data-id="element-338">Duyuruyu Sil</h3>
              <p className="mb-6" data-id="element-339">
                <span className="text-pink-400 font-semibold" data-id="element-340">
                  "{announcementToDelete?.title}"
                </span>{' '}
                başlıklı duyuruyu silmek istediğinize emin misiniz? Bu işlem
                geri alınamaz.
              </p>
              <div className="flex justify-end gap-3" data-id="element-341">
                <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors" data-id="element-342">
                  İptal
                </button>
                <button onClick={confirmDelete} className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 hover:opacity-90 text-white transition-colors" data-id="element-343">
                  Evet, Sil
                </button>
              </div>
              <button onClick={() => setShowDeleteModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white" data-id="element-344">
                <X className="w-5 h-5" data-id="element-345" />
              </button>
            </div>
          </AuroraFloatingCard>
        </div>}
      {/* CSS Animation */}
      <style jsx data-id="element-346">{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s forwards;
        }
      `}</style>
    </div>;
}
export default DuyuruYonetimi;